import { api } from "./api";
import { ExtractionColumnDetail, Project, ReportEnqueueResponse, ReportStatusResponse, TemplateDocumentData } from "@/types/models";

interface ProjectApiResponse {
  id: string;
  name: string;
  code: string;
  kantata_project_id: string | null;
  client_ids: string[];
  client_names: string[];
  status: string;
  start_date: string;
  team_members: number;
  therapy_area: string | null;
  indication: string | null;
  condition: string | null;
  objective: string | null;
  project_type: string | null;
  model: string;
  data_retention_default: string | null;
  completion: number;
  days_in_progress: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  member_ids: string[];
}

function toProject(r: ProjectApiResponse): Project {
  return {
    id: r.id,
    name: r.name,
    code: r.code,
    kantataProjectId: r.kantata_project_id ?? null,
    clientIds: r.client_ids ?? [],
    clientNames: r.client_names ?? [],
    status: r.status,
    startDate: r.start_date,
    teamMembers: r.team_members,
    therapyArea: r.therapy_area || "",
    indication: r.indication || "",
    condition: r.condition || "",
    objective: r.objective || "",
    projectType: r.project_type || "",
    model: r.model,
    dataRetentionDefault: r.data_retention_default ?? null,
    completion: r.completion,
    daysInProgress: r.days_in_progress,
    memberIds: r.member_ids ?? [],
    createdBy: r.created_by,
    documents: [],
    picoExtractions: [],
    createdAt: r.created_at,
  };
}

interface TemplateDocumentDataApiResponse {
  id: string;
  project_id: string;
  project_template_id: string;
  document_id: string;
  data: string | null;
  status: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function toTemplateDocumentData(r: TemplateDocumentDataApiResponse): TemplateDocumentData {
  return {
    id: r.id,
    projectId: r.project_id,
    projectTemplateId: r.project_template_id,
    documentId: r.document_id,
    data: r.data,
    status: r.status,
    isActive: r.is_active,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export interface ProjectCreateInput {
  name: string;
  code: string;
  kantataProjectId: string;
  clientIds?: string[];
  startDate: string;
  therapyArea?: string;
  indication?: string;
  condition?: string;
  objective?: string;
  projectType?: string;
  model?: string;
  dataRetentionDefault?: string | null;
  memberIds?: string[];
}

export interface ProjectDocumentsResponse {
  inserted: number;
  updated: number;
  deleted: number;
  total: number;
}

export interface ArtifactItem {
  uid: string;
  name: string;
  type: string;
  section?: string | null;
  /** Sheet the artifact lives on. section is the visible tab name;
   *  sheetCodeName is the VBA code name, stable across tab renames, so it's
   *  the grouping key. tabNumber orders groups in workbook order. */
  sheetCodeName?: string | null;
  tabNumber?: number | null;
  /** What the pill will read once this upload is confirmed, and whether the
   *  upload adds or changes this artefact. Both decided by the API so the
   *  preview table and the cards cannot disagree about what counts as changed. */
  uploadVersion?: number | null;
  changed?: boolean;
}

interface ArtifactItemApiResponse {
  uid: string;
  name: string;
  type: string;
  section: string | null;
  sheet_code_name: string | null;
  tab_number: number | null;
  upload_version: number | null;
  changed: boolean;
}

function toArtifactItem(r: ArtifactItemApiResponse): ArtifactItem {
  return {
    uid: r.uid,
    name: r.name,
    type: r.type,
    section: r.section,
    sheetCodeName: r.sheet_code_name,
    tabNumber: r.tab_number,
    uploadVersion: r.upload_version,
    changed: r.changed,
  };
}

export interface ArtifactPreview {
  sourceFilename: string;
  artifacts: ArtifactItem[];
}

/** Where an artifact is in the pipeline.
 *
 *  "Pending Review" is the database column's default and predates the pipeline;
 *  nothing writes it today, but rows created before enrichment existed still
 *  carry it, so it has to be a legal value rather than an unreachable one. */
export type ArtifactStatus =
  | "Pending Review"
  | "Queued"
  | "Processing"
  | "Ready"
  | "Error";

export interface Artifact {
  id: string;
  projectId: string;
  uid: string;
  name: string;
  type: string;
  section: string | null;
  sheetCodeName: string | null;
  tabNumber: number | null;
  uploadVersion: number | null;
  sourceFilename: string;
  /** Durable blob reference. downloadUrl is a SAS that expires an hour after
   *  extraction, so content is fetched through the artifact file endpoint,
   *  which re-signs per request. */
  blobName: string | null;
  contentType: string | null;
  /** Model-generated, for images and charts. Populated by enrichment, which
   *  runs after extraction, so it is absent until then. */
  description: string | null;
  /** Why this artifact failed, when status is Error. */
  error: string | null;
  rowCount: number | null;
  columnCount: number | null;
  downloadUrl: string | null;
  status: ArtifactStatus;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ArtifactApiResponse {
  id: string;
  project_id: string;
  uid: string;
  name: string;
  type: string;
  section: string | null;
  sheet_code_name: string | null;
  tab_number: number | null;
  upload_version: number | null;
  source_filename: string;
  blob_name: string | null;
  content_type: string | null;
  description: string | null;
  error: string | null;
  row_count: number | null;
  column_count: number | null;
  download_url: string | null;
  status: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function toArtifact(r: ArtifactApiResponse): Artifact {
  return {
    id: r.id,
    projectId: r.project_id,
    uid: r.uid,
    name: r.name,
    type: r.type,
    section: r.section,
    sheetCodeName: r.sheet_code_name,
    tabNumber: r.tab_number,
    uploadVersion: r.upload_version,
    sourceFilename: r.source_filename,
    blobName: r.blob_name,
    contentType: r.content_type,
    description: r.description,
    error: r.error,
    rowCount: r.row_count,
    columnCount: r.column_count,
    downloadUrl: r.download_url,
    // Asserted, not validated. The column is a plain string server-side, so an
    // unexpected value is possible in principle; it would fall through every
    // branch to the settled state, which is the right place to land.
    status: r.status as ArtifactStatus,
    isActive: r.is_active,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export const projectsApi = {
  list: async (): Promise<Project[]> => {
    const data = await api.get<ProjectApiResponse[]>("/api/projects");
    return data.map(toProject);
  },

  get: async (id: string): Promise<Project> => {
    const data = await api.get<ProjectApiResponse>(`/api/projects/${id}`);
    return toProject(data);
  },

  create: async (project: ProjectCreateInput): Promise<Project> => {
    const body = {
      name: project.name,
      code: project.code,
      kantata_project_id: project.kantataProjectId,
      client_ids: project.clientIds ?? [],
      start_date: project.startDate,
      therapy_area: project.therapyArea,
      indication: project.indication,
      condition: project.condition,
      objective: project.objective,
      project_type: project.projectType,
      model: project.model ?? "opus-46",
      data_retention_default: project.dataRetentionDefault ?? null,
      member_ids: project.memberIds ?? [],
    };
    const data = await api.post<ProjectApiResponse>("/api/projects", body);
    return toProject(data);
  },

  update: async (
    id: string,
    project: Partial<ProjectCreateInput> & {
      status?: string;
      teamMembers?: number;
      completion?: number;
      daysInProgress?: number;
    }
  ): Promise<Project> => {
    const body: Record<string, unknown> = {};
    if (project.name !== undefined) body.name = project.name;
    if (project.code !== undefined) body.code = project.code;
    if (project.kantataProjectId !== undefined)
      body.kantata_project_id = project.kantataProjectId;
    if (project.clientIds !== undefined)
      body.client_ids = project.clientIds;
    if (project.status !== undefined) body.status = project.status;
    if (project.teamMembers !== undefined)
      body.team_members = project.teamMembers;
    if (project.therapyArea !== undefined)
      body.therapy_area = project.therapyArea;
    if (project.indication !== undefined)
      body.indication = project.indication;
    if (project.condition !== undefined)
      body.condition = project.condition;
    if (project.objective !== undefined) body.objective = project.objective;
    if (project.projectType !== undefined)
      body.project_type = project.projectType;
    if (project.model !== undefined) body.model = project.model;
    if (project.dataRetentionDefault !== undefined)
      body.data_retention_default = project.dataRetentionDefault;
    if (project.completion !== undefined)
      body.completion = project.completion;
    if (project.daysInProgress !== undefined)
      body.days_in_progress = project.daysInProgress;
    if (project.memberIds !== undefined)
      body.member_ids = project.memberIds;
    const data = await api.put<ProjectApiResponse>(
      `/api/projects/${id}`,
      body
    );
    return toProject(data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/projects/${id}`);
  },

  insertDocuments: async (
    projectId: string,
    documentIds: string[]
  ): Promise<ProjectDocumentsResponse> => {
    return api.post<ProjectDocumentsResponse>("/api/projects/insert-documents", {
      item: { project_id: projectId, document_ids: documentIds },
    });
  },

  deleteDocuments: async (
    projectId: string,
    documentIds: string[]
  ): Promise<ProjectDocumentsResponse> => {
    return api.post<ProjectDocumentsResponse>("/api/projects/delete-documents", {
      item: { project_id: projectId, document_ids: documentIds },
    });
  },

  getDocumentIds: async (projectId: string): Promise<string[]> => {
    try {
      const data = await api.get<unknown>(`/api/projects/${projectId}/documents`);
      if (!Array.isArray(data) || data.length === 0) return [];
      const first = data[0];
      // Plain string array: ["id1", "id2"]
      if (typeof first === "string") return data;
      // Object array: [{id: "..."}, ...] or [{document_id: "..."}, ...]
      if (first && typeof first === "object") {
        return (data as Record<string, unknown>[])
          .map((d) => (d.id ?? d.document_id) as string)
          .filter(Boolean);
      }
      return [];
    } catch {
      return [];
    }
  },

  reorderProjectPromptLibraries: async (
    projectId: string,
    orderedIds: string[]
  ): Promise<void> => {
    await api.post(`/api/projects/${projectId}/prompt-libraries/reorder`, {
      ordered_ids: orderedIds,
    });
  },

  updateProjectPromptLibrary: async (
    projectId: string,
    promptLibraryId: string,
    data: { name?: string; priming?: string; description?: string; parent_id?: string | null; sources?: { document_ids: string[]; extraction_point_ids: string[]; artifact_ids?: string[] }; description_context?: string; description_ai_output?: string }
  ): Promise<void> => {
    await api.patch(`/api/projects/${projectId}/prompt-libraries/${promptLibraryId}`, data);
  },

  createProjectPromptLibrary: async (
    projectId: string,
    data: { name: string; description?: string; parent_id?: string; template_id?: string }
  ): Promise<{ id: string; name: string; description: string | null; parent_id: string | null; ordinal: number | null }> => {
    return api.post(`/api/projects/${projectId}/prompt-libraries`, data);
  },

  deleteProjectPromptLibrary: async (
    projectId: string,
    promptLibraryId: string
  ): Promise<void> => {
    await api.delete(`/api/projects/${projectId}/prompt-libraries/${promptLibraryId}`);
  },

  createProjectTemplate: async (
    projectId: string,
    templateId: string,
    formatType?: string
  ): Promise<{ inserted: number; deactivated: number; total: number }> => {
    const url = formatType
      ? `/api/projects/${projectId}/templates/${templateId}?format_type=${encodeURIComponent(formatType)}`
      : `/api/projects/${projectId}/templates/${templateId}`;
    return api.post<{ inserted: number; deactivated: number; total: number }>(url, {});
  },

  getActiveProjectTemplates: async (
    projectId: string,
    formatType?: string
  ): Promise<{ id: string; source_template_id: string; name: string; description: string | null; format_type: string | null }[]> => {
    const url = formatType
      ? `/api/projects/${projectId}/templates/active?format_type=${encodeURIComponent(formatType)}`
      : `/api/projects/${projectId}/templates/active`;
    return api.get(url);
  },

  getActiveProjectTemplatesWithPrompts: async (
    projectId: string,
    formatType?: string
  ): Promise<{
    template: { id: string; source_template_id: string; name: string; description: string | null; format_type: string | null };
    prompt_libraries: { id: string; source_prompt_library_id: string; name: string; priming: string | null; ordinal: number | null; description: string | null; parent_id: string | null; description_context: string | null; description_ai_output: string | null; sources: { document_ids: string[]; extraction_point_ids: string[]; artifact_ids?: string[] } | null }[];
  }[]> => {
    try {
      const url = formatType
        ? `/api/projects/${projectId}/templates/active-with-prompts?format_type=${encodeURIComponent(formatType)}`
        : `/api/projects/${projectId}/templates/active-with-prompts`;
      return await api.get(url);
    } catch (err) {
      console.error("Failed to load active project templates with prompts:", err);
      return [];
    }
  },

  bulkCreateProjectTemplates: async (
    projectId: string,
    templateIds: string[],
    formatType?: string
  ): Promise<{ inserted: number; deactivated: number; total: number }> => {
    return api.post(`/api/projects/${projectId}/templates/bulk`, {
      template_ids: templateIds,
      format_type: formatType ?? null,
    });
  },

  createCustomProjectTemplate: async (
    projectId: string
  ): Promise<{ inserted: number; deactivated: number; total: number }> => {
    return api.post(`/api/projects/${projectId}/templates`, {});
  },

  getProjectTemplate: async (projectId: string, formatType?: string): Promise<{
    template: { id: string; source_template_id: string; name: string; description: string | null; format_type: string | null };
    prompt_libraries: { id: string; source_prompt_library_id: string; name: string; priming: string | null; ordinal: number | null; description: string | null; parent_id: string | null; description_context: string | null; description_ai_output: string | null; sources: { document_ids: string[]; extraction_point_ids: string[]; artifact_ids?: string[] } | null }[];
  } | null> => {
    try {
      const url = formatType
        ? `/api/projects/${projectId}/templates?format_type=${encodeURIComponent(formatType)}`
        : `/api/projects/${projectId}/templates`;
      return await api.get<{
        template: { id: string; source_template_id: string; name: string; description: string | null; format_type: string | null };
        prompt_libraries: { id: string; source_prompt_library_id: string; name: string; priming: string | null; ordinal: number | null; description: string | null; parent_id: string | null; description_context: string | null; description_ai_output: string | null; sources: { document_ids: string[]; extraction_point_ids: string[]; artifact_ids?: string[] } | null }[];
      }>(url);
    } catch {
      return null;
    }
  },

  getReviewConfig: (projectId: string): Promise<{ api_name: string | null }> =>
    api.get(`/api/projects/${projectId}/templates/review-config`),

  getTemplateDocuments: async (
    projectId: string,
    templateId: string,
    activeOnly: boolean = true
  ): Promise<TemplateDocumentData[]> => {
    const data = await api.get<TemplateDocumentDataApiResponse[]>(
      `/api/projects/${projectId}/templates/${templateId}/documents?active_only=${activeOnly}`
    );
    return data.map(toTemplateDocumentData);
  },

  createTemplateDocument: async (
    projectId: string,
    templateId: string,
    documentId: string,
    data: string | null,
    status: string = "Processed"
  ): Promise<TemplateDocumentData> => {
    const body = {
      document_id: documentId,
      data: data,
      status: status,
    };
    const response = await api.post<TemplateDocumentDataApiResponse>(
      `/api/projects/${projectId}/templates/${templateId}/documents`,
      body
    );
    return toTemplateDocumentData(response);
  },

  updateTemplateDocument: async (
    projectId: string,
    templateId: string,
    documentDataId: string,
    data: string | null,
    status?: string
  ): Promise<TemplateDocumentData> => {
    const body: Record<string, unknown> = {};
    if (data !== undefined) body.data = data;
    if (status !== undefined) body.status = status;

    const response = await api.patch<TemplateDocumentDataApiResponse>(
      `/api/projects/${projectId}/templates/${templateId}/documents/${documentDataId}`,
      body
    );
    return toTemplateDocumentData(response);
  },

  deleteTemplateDocument: async (
    projectId: string,
    templateId: string,
    documentDataId: string
  ): Promise<void> => {
    await api.delete(
      `/api/projects/${projectId}/templates/${templateId}/documents/${documentDataId}`
    );
  },

  deleteAllTemplateDocuments: async (
    projectId: string,
    templateId: string
  ): Promise<void> => {
    await api.delete(`/api/projects/${projectId}/templates/${templateId}/documents`);
  },

  enqueueExtract: async (
    projectId: string,
    body: {
      template_id: string;
      documents: {
        document_id: string;
        sourcefile: string;
        columns: { column_name: string; question: string }[];
        priming?: string;
        model?: string;
      }[];
    }
  ): Promise<{ enqueued: { document_id: string; sentinel_id: string }[]; queue_size: number }> =>
    api.post(`/api/projects/${projectId}/enqueue-extract`, body),

  getExtractionStatus: (
    projectId: string,
    templateId: string
  ): Promise<{
    statuses: Record<string, { status: string; queue_position?: number }>;
    all_done: boolean;
    queue_size: number;
  }> =>
    api.get(
      `/api/projects/${projectId}/extraction-status?template_id=${encodeURIComponent(templateId)}`
    ),

  cancelExtraction: (projectId: string, templateId: string): Promise<{ cancelled: number }> =>
    api.delete(
      `/api/projects/${projectId}/extraction-sentinels?template_id=${encodeURIComponent(templateId)}`
    ),

  createExtractionBatch: async (
    projectId: string,
    batches: {
      template_id: string;
      documents: {
        document_id: string;
        sourcefile: string;
        columns: { column_name: string; question: string }[];
        priming?: string;
        model?: string;
      }[];
    }[]
  ): Promise<{ batch_id: string; template_statuses: Record<string, string> }> =>
    api.post(`/api/projects/${projectId}/extraction-batches`, { batches }),

  getExtractionBatchStatus: (
    projectId: string
  ): Promise<{
    batch_id: string | null;
    template_statuses: Record<string, string>;
    /** Document ids each template in the batch is (re)processing — may be a subset of the project's documents. */
    template_document_ids?: Record<string, string[]>;
    all_done: boolean;
  }> =>
    api.get(`/api/projects/${projectId}/extraction-batches/active-status`),

  cancelExtractionBatch: (
    projectId: string,
    batchId: string
  ): Promise<{ cancelled: boolean }> =>
    api.delete(`/api/projects/${projectId}/extraction-batches/${encodeURIComponent(batchId)}`),

  enqueueGenerateReport: (
    projectId: string,
    body: {
      project_id: string;
      template_id: string;
      prompt_library_id: string;
      priming: string;
      document_ids: string[];
      extraction_column_details: ExtractionColumnDetail[];
      reporting_column_name: string;
      model: string;
    }
  ): Promise<ReportEnqueueResponse> =>
    api.post<ReportEnqueueResponse>(
      `/api/projects/${projectId}/enqueue-generate-report`,
      body
    ),

  getReportStatus: (
    projectId: string,
    params: { template_id: string; prompt_library_id: string }
  ): Promise<ReportStatusResponse> =>
    api.get<ReportStatusResponse>(
      `/api/projects/${projectId}/report-status?template_id=${encodeURIComponent(params.template_id)}&prompt_library_id=${encodeURIComponent(params.prompt_library_id)}`
    ),

  saveReportData: (
    projectId: string,
    body: { template_id: string; prompt_library_id: string; data: string }
  ): Promise<{ ok: boolean }> =>
    api.patch<{ ok: boolean }>(`/api/projects/${projectId}/report-data`, body),

  getTemplateUsageCount: async (templateId: string): Promise<{ usage_count: number }> => {
    try {
      const data = await api.get<{ usage_count: number }>(`/api/projects/usage-count?source_template_id=${templateId}`);
      return data;
    } catch {
      return { usage_count: 0 };
    }
  },

  getPromptUsageCount: async (promptLibraryId: string): Promise<{ usage_count: number }> => {
    try {
      const data = await api.get<{ usage_count: number }>(`/api/projects/usage-count?source_prompt_library_id=${promptLibraryId}`);
      return data;
    } catch {
      return { usage_count: 0 };
    }
  },

  previewArtifacts: async (projectId: string, file: File): Promise<ArtifactPreview> => {
    const formData = new FormData();
    formData.append("file", file);
    const data = await api.postFormData<{ source_filename: string; artifacts: ArtifactItemApiResponse[] }>(
      `/api/projects/${projectId}/artifacts/preview`,
      formData
    );
    return { sourceFilename: data.source_filename, artifacts: data.artifacts.map(toArtifactItem) };
  },

  // Multipart, not JSON: extraction reads the workbook, and preview's copy is
  // discarded so a rejected upload leaves nothing behind — so the file is sent
  // again here. selected_uids goes as a JSON string because multipart form
  // fields have no list type.
  confirmArtifacts: async (
    projectId: string,
    file: File,
    selectedUids: string[]
  ): Promise<Artifact[]> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("selected_uids", JSON.stringify(selectedUids));
    const data = await api.postFormData<ArtifactApiResponse[]>(
      `/api/projects/${projectId}/artifacts/confirm`,
      formData
    );
    return data.map(toArtifact);
  },

  activateArtifacts: async (projectId: string, uids: string[]): Promise<void> => {
    await api.post(`/api/projects/${projectId}/artifacts/activate`, { uids });
  },

  deactivateArtifacts: async (projectId: string, uids: string[]): Promise<void> => {
    await api.post(`/api/projects/${projectId}/artifacts/deactivate`, { uids });
  },

  updateArtifactDescription: async (
    projectId: string,
    uid: string,
    description: string
  ): Promise<Artifact> => {
    const data = await api.patch<ArtifactApiResponse>(
      `/api/projects/${projectId}/artifacts/${encodeURIComponent(uid)}/description`,
      { description }
    );
    return toArtifact(data);
  },

  deleteArtifacts: async (projectId: string, uids: string[]): Promise<void> => {
    await api.post(`/api/projects/${projectId}/artifacts/delete`, { uids });
  },

  /** Fetches an artifact's extracted content (CSV for tables, image bytes otherwise).
   *  Goes through the file endpoint because the stored download_url SAS expires. */
  getArtifactFile: (projectId: string, uid: string, version?: string | null): Promise<Blob> =>
    api.getBlob(
      `/api/projects/${projectId}/artifacts/${encodeURIComponent(uid)}/file` +
        (version ? `?v=${encodeURIComponent(version)}` : "")
    ),

  getArtifacts: async (projectId: string): Promise<Artifact[]> => {
    const data = await api.get<ArtifactApiResponse[]>(`/api/projects/${projectId}/artifacts`);
    return data.map(toArtifact);
  },

  generateMarkdownWord: async (
    content: string,
    filename: string,
    docStyle?: Record<string, unknown>,
    coverPage?: { is_coverpage_required: boolean; coverpage_content: string; cover_image_base64?: string },
    customProperties?: Record<string, string>
  ): Promise<void> => {
    const blob = await api.postBlob("/api/generate-markdown-word", {
      content,
      filename,
      ...(docStyle   ? { doc_style: docStyle } : {}),
      ...(coverPage  ? coverPage              : {}),
      ...(customProperties ? { custom_properties: customProperties } : {}),
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export const LUMANITY_DOC_STYLE: Record<string, unknown> = {
  heading_font: "Arial",
  heading_bold: false,
  h1_color: "#061D49",
  h1_size_pt: 26,
  h1_bold: true,
  h2_color: "#0066FF",
  h2_size_pt: 14,
  h3_color: "#1F579D",
  h3_size_pt: 12,
  h4_color: "#1F579D",
  body_font: "Arial",
  body_size_pt: 11,
  body_color: "#061D49",
  line_spacing: 1.5,
  cover_body_size_pt: 18,
  bullet_char: "■",
  bullet_color: "#0066FF",
  hr_as_page_break: true,
  footer_text: "Confidential document prepared by Lumanity",
  table_header_bg: "#061D49",
  table_header_fg: "#FFFFFF",
  table_border_color: "#1F579D",
  reference_size_pt: 9,
};
